import type { ReactNode } from 'react';
import {
	CheckCircleOutlined,
	ClockCircleOutlined,
	CloseCircleOutlined,
	FileUnknownOutlined,
	ReloadOutlined,
	SyncOutlined,
} from '@ant-design/icons';
import { Button, Flex, List, Tag, Typography } from 'antd';

const { Text } = Typography;

/**
 * One row of the analysis step: the subset of the order-line-item list payload
 * the Step 2 polling UI needs. Statuses mirror the backend
 * `OrderLineItem.INTERNAL_ANALYSIS_*` constants. Also consumed by the draft
 * resume banner (`InternalDraftBanner`), which additionally needs to know
 * whether the 3D/drawing files themselves are present to offer an Upload
 * control per row.
 */
export type AnalysisLineItem = {
	id: string;
	name: string;
	internal_file_analysis_status:
		| 'none'
		| 'pending'
		| 'in_progress'
		| 'completed'
		| 'failed';
	internal_step_file?: string | null;
	internal_drawing_file?: string | null;
	internal_analysis_error?: string | null;
};

type AnalysisStatus = AnalysisLineItem['internal_file_analysis_status'];

/**
 * Finalize is only offered when the order has at least one line item and every
 * one of them finished analysis; the backend enforces the same rule (409
 * `not_ready`) so this is a UX shortcut, not the source of truth.
 */
export const finalizeEnabled = (items: AnalysisLineItem[]) =>
	items.length > 0 &&
	items.every((i) => i.internal_file_analysis_status === 'completed');

/**
 * How many rows finished analysis — the numerator of both progress readouts
 * ("N of M analyzed" in the wizard's Step 2, "Draft — N/M line items analyzed"
 * in the draft resume banner), shared so the two can't drift apart.
 */
export const analyzedCount = (items: AnalysisLineItem[]) =>
	items.filter((i) => i.internal_file_analysis_status === 'completed').length;

/**
 * One chip per backend status. The labels are load-bearing: the e2e specs and
 * the unit tests assert on these exact strings ("No 3D file" / "Queued" /
 * "Analyzing…" / "Analyzed"), so they only ever change together with those
 * assertions.
 */
const STATUS_CHIPS: Record<
	AnalysisStatus,
	{ label: string; color?: string; icon: ReactNode }
> = {
	none: { label: 'No 3D file', icon: <FileUnknownOutlined /> },
	pending: { label: 'Queued', icon: <ClockCircleOutlined /> },
	in_progress: {
		label: 'Analyzing…',
		color: 'processing',
		icon: <SyncOutlined spin />,
	},
	completed: {
		label: 'Analyzed',
		color: 'success',
		icon: <CheckCircleOutlined />,
	},
	failed: { label: 'Failed', color: 'error', icon: <CloseCircleOutlined /> },
};

/**
 * The status chip for a single line item, shared by the wizard's Step 2 and the
 * draft resume banner so a row reads identically wherever it shows up. Unknown
 * statuses (a backend that grew a new one) render nothing rather than an empty
 * chip.
 */
export const AnalysisStatusTag = ({ status }: { status: AnalysisStatus }) => {
	const chip = STATUS_CHIPS[status];
	if (!chip) return null;
	return (
		<Tag color={chip.color} icon={chip.icon} style={{ marginInlineEnd: 0 }}>
			{chip.label}
		</Tag>
	);
};

export const AnalysisRows = ({
	lineItems,
	onRetry,
}: {
	lineItems: AnalysisLineItem[];
	onRetry: (id: string) => void;
}) => (
	<List
		size="small"
		bordered
		dataSource={lineItems}
		rowKey={(li) => li.id}
		renderItem={(li) => (
			<List.Item
				actions={
					li.internal_file_analysis_status === 'failed'
						? [
								<Button
									key="retry"
									danger
									size="small"
									icon={<ReloadOutlined />}
									onClick={() => onRetry(li.id)}
								>
									Retry
								</Button>,
							]
						: undefined
				}
			>
				<Flex vertical gap={2} className="flex-1 min-w-0">
					<Flex align="center" justify="space-between" gap={8}>
						<Text strong>{li.name}</Text>
						<AnalysisStatusTag status={li.internal_file_analysis_status} />
					</Flex>
					{li.internal_file_analysis_status === 'failed' &&
						li.internal_analysis_error && (
							<Text type="danger" style={{ fontSize: 12 }}>
								{li.internal_analysis_error}
							</Text>
						)}
				</Flex>
			</List.Item>
		)}
	/>
);
